import { GUEST_UPGRADE_ID_HEADER } from '@argus-labs/auth-shared/auth'
import type { z } from 'zod'
import { authClient } from './auth-client'
import type { commonSearchSchema } from './utils'

export type AuthFlow =
  | { kind: 'account' }
  | { kind: 'handoff'; id: string }
  | { kind: 'guestUpgrade'; id: string }

export function authFlow(search: z.infer<typeof commonSearchSchema>): AuthFlow {
  if (search.upgradeId) return { kind: 'guestUpgrade', id: search.upgradeId }
  if (search.handoffId) return { kind: 'handoff', id: search.handoffId }
  return { kind: 'account' }
}

export function flowHeaders(flow: AuthFlow) {
  return flow.kind === 'guestUpgrade' ? { [GUEST_UPGRADE_ID_HEADER]: flow.id } : undefined
}

// Flow IDs are retained across routes, so each navigation sets at most one and clears the rest.
const noFlowIds = { handoffId: undefined, upgradeId: undefined }

// The success page's search: which flow finished, with no flow left in progress.
export function completedSearch(flow: AuthFlow) {
  return { ...noFlowIds, completed: flow.kind }
}

export function flowDestination(flow: AuthFlow) {
  switch (flow.kind) {
    case 'guestUpgrade':
      return { to: '/success', search: completedSearch(flow) } as const
    case 'handoff':
      return { to: '/authorize', search: { ...noFlowIds, handoffId: flow.id } } as const
    case 'account':
      return { to: '/account' } as const
  }
}

// Why the server refused to save guest progress, from its error code; undefined for other errors.
const upgradeFailureReasons = {
  PLAYER_IDENTITY_CONFLICT: 'conflict',
  GUEST_UPGRADE_INVALID: 'invalid',
  GUEST_UPGRADE_ALREADY_COMPLETED: 'alreadySaved',
} as const

export type UpgradeFailureReason =
  (typeof upgradeFailureReasons)[keyof typeof upgradeFailureReasons]

export function upgradeFailureReason(flow: AuthFlow, code: string | undefined) {
  if (flow.kind !== 'guestUpgrade' || !code || !Object.hasOwn(upgradeFailureReasons, code)) return
  return upgradeFailureReasons[code as keyof typeof upgradeFailureReasons]
}

export async function authorizeFlow(flow: AuthFlow) {
  if (flow.kind === 'handoff') {
    const result = await authClient.authHandoff.authorize({ handoffId: flow.id })
    return { success: !!result.data?.success, error: result.error }
  }
  return { success: false, error: null }
}
